import dayjs from "dayjs"
import { useCallback, useMemo } from "react"
import {
  useGetCasemixCategoriesQuery,
  useGetPractitionerCasemixQuery,
} from "../generated/graphql"
import { colors } from "../utils/theme"
import { useGraphQLClient } from "./useGraphQLClient"

/**
 * Holds a casemix.display_order to count map
 */
export type CasemixData = Map<number, number>

/**
 * Represents the casemix data mapped by practitioner_id.
 */
export type CasemixByPractitioner = Map<string, CasemixData>

/**
 * Custom hook that retrieves casemix data based on a given timestamp.
 *
 * @param sinceTimestamp - The timestamp indicating the starting point for retrieving casemix data.
 * @returns An object containing casemix data grouped by practitioner and organization, and a function to retrieve casemix data for a given practitioner.
 */
export default function useCasemix(sinceTimestamp?: string) {
  const graphQLClient = useGraphQLClient()
  const { data: casemixByPractitionerData } = useGetPractitionerCasemixQuery(
    graphQLClient,
    {
      completed_after:
        sinceTimestamp ??
        dayjs().startOf("day").subtract(31, "days").toISOString(), // I was worried this would cause a lot of rerenders but it seems to be fine.
    }
  )
  const { data: casemixDisplayValueData } =
    useGetCasemixCategoriesQuery(graphQLClient)

  /**
   * Map of category display values.
   * @type {Map<number, { color: string; category: string }>}
   */
  const categoryDisplayValues = useMemo(() => {
    const casemixDisplayValues = new Map<
      number,
      { color: string; category: string }
    >()
    casemixDisplayValueData?.casemix_category?.forEach((row) => {
      const displayOrder = row?.display_order
      const category = row?.category
      const color = row?.display_color ?? colors.neutral.clay
      if (displayOrder !== undefined && displayOrder !== null && category) {
        casemixDisplayValues.set(displayOrder, { color, category })
      }
    })
    return casemixDisplayValues
  }, [casemixDisplayValueData])

  /**
   * Calculates casemix data by practitioner and organization.
   *
   * @returns An array containing two maps: casemixByPractitioner and caseMixForOrg.
   * - casemixByPractitioner: A map that stores casemix data grouped by practitioner.
   * - caseMixForOrg: A map that stores casemix data for the organization.
   */
  const [casemixByPractitioner, casemixForOrg] = useMemo(() => {
    // Calculate the maximum category for each user and store the users by practitioner
    const usersByPractitioner: Map<string, string[]> = new Map()
    const maximumCategoryForUser = new Map<string, number>()
    casemixByPractitionerData?.vw_questionnaire_display_values?.forEach(
      (row) => {
        if (
          row?.practitioner_id &&
          row?.user_id &&
          row?.casemix_display_order !== null &&
          row?.casemix_display_order !== undefined
        ) {
          const users = usersByPractitioner.get(row.practitioner_id) ?? []
          const userMaxCategory = maximumCategoryForUser.get(row.user_id) ?? 0
          users.push(row.user_id)
          usersByPractitioner.set(row.practitioner_id, users)
          maximumCategoryForUser.set(
            row.user_id,
            Math.max(userMaxCategory, row.casemix_display_order)
          )
        }
      }
    )
    // Calculate casemixByPractitioner and caseMixForOrg
    const casemixByPractitioner: CasemixByPractitioner = new Map()
    const casemixForOrg: CasemixData = new Map()
    usersByPractitioner.forEach((users, practitionerId) => {
      users.forEach((userId) => {
        // casemixByPractitioner
        const maxCategory = maximumCategoryForUser.get(userId) ?? 0
        const practitionerCasemixMap =
          casemixByPractitioner.get(practitionerId) ?? new Map()
        const countForCategory = practitionerCasemixMap.get(maxCategory) ?? 0
        practitionerCasemixMap.set(maxCategory, countForCategory + 1)
        casemixByPractitioner.set(practitionerId, practitionerCasemixMap)
        // caseMixForOrg
        const currentOrgCategoryCount = casemixForOrg.get(maxCategory) ?? 0
        casemixForOrg.set(maxCategory, currentOrgCategoryCount + 1)
      })
    })
    return [casemixByPractitioner, casemixForOrg]
  }, [casemixByPractitionerData])

  /**
   * Retrieves the casemix for a given practitioner.
   * @param practitionerId - The ID of the practitioner.
   * @returns A map representing the casemix for the practitioner.
   */
  const getCasemixForPractitioner = useCallback(
    (practitionerId: string): CasemixData => {
      return casemixByPractitioner.get(practitionerId) ?? new Map()
    },
    [casemixByPractitioner]
  )

  return {
    casemixByPractitioner,
    casemixForOrg,
    getCasemixForPractitioner,
    categoryDisplayValues,
  }
}
